/**
 * ShoppingPage
 * Kitchly v0.5.0
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Plus,
  ShoppingCart,
  Check,
  PackageOpen,
  ArrowRight,
  Trash2,
  ShoppingBag
} from 'lucide-react';
import { shopping } from '../services/api';

function ShoppingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [standbyCount, setStandbyCount] = useState(0);
  const [newItemName, setNewItemName] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const data = await shopping.list();
      setItems(data.items || []);
      setStandbyCount(data.standby_count || 0);
    } catch {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newItemName.trim()) return;
    setAdding(true);

    try {
      const item = await shopping.addItem({ name: newItemName.trim() });
      setItems(prev => [...prev, item]);
      setNewItemName('');
      toast.success(t('shopping.itemAdded', { name: newItemName.trim() }));
    } catch {
      toast.error(t('common.error'));
    } finally {
      setAdding(false);
    }
  };

  const handleCheck = async (item) => {
    try {
      if (item.status === 'pending') {
        await shopping.check(item.id);
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: 'checked' } : i));
      } else {
        await shopping.uncheck(item.id);
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: 'pending' } : i));
      }
    } catch (err) {
      if (err.status === 404) {
        setItems(prev => prev.filter(i => i.id !== item.id));
        toast.error('Item no longer exists');
      } else {
        toast.error(t('common.error'));
      }
    }
  };

  const handleRemove = async (item) => {
    try {
      await shopping.removeItem(item.id);
      setItems(prev => prev.filter(i => i.id !== item.id));
      toast.success(t('shopping.itemRemoved'));
    } catch (err) {
      if (err.status === 404) {
        setItems(prev => prev.filter(i => i.id !== item.id));
        toast.error('Item no longer exists');
      } else {
        toast.error(t('common.error'));
      }
    }
  };

  const handleCheckout = async () => {
    const checkedCount = items.filter(i => i.status === 'checked').length;
    if (checkedCount === 0) return;

    try {
      const result = await shopping.checkout();
      toast.success(t('shopping.checkoutDone', { count: result.items_moved }));
      loadData();
    } catch {
      toast.error(t('common.error'));
    }
  };

  const pendingItems = items.filter(i => i.status === 'pending');
  const checkedItems = items.filter(i => i.status === 'checked');

  if (loading) {
    return <div className="page-loader"><div className="loader-spinner" /></div>;
  }

  return (
    <div className="shopping-page">
      <div className="page-header">
        <h1>{t('shopping.title')}</h1>
      </div>

      {/* Standby banner */}
      {standbyCount > 0 && (
        <div
          className="banner banner-accent"
          onClick={() => navigate('/put-away')}
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
        >
          <span>
            <PackageOpen size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            {t('shopping.standbyBanner', { count: standbyCount })}
          </span>
          <ArrowRight size={18} />
        </div>
      )}

      {/* Add item form */}
      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 'var(--space-sm)', marginBottom: 'var(--space-lg)' }}>
        <input
          type="text"
          className="form-input"
          placeholder={t('shopping.addItem')}
          value={newItemName}
          onChange={(e) => setNewItemName(e.target.value)}
          style={{ flex: 1 }}
        />
        <button type="submit" className="btn btn-primary" disabled={adding || !newItemName.trim()}>
          <Plus size={18} />
        </button>
      </form>

      {/* Items */}
      {items.length === 0 ? (
        <div className="empty-state">
          <ShoppingCart size={64} className="empty-state-icon" />
          <h3>{t('shopping.empty')}</h3>
        </div>
      ) : (
        <>
          {/* Pending items */}
          {pendingItems.map(item => (
            <div key={item.id} className="list-item">
              <button
                className="btn-icon"
                onClick={() => handleCheck(item)}
                style={{
                  width: '28px', height: '28px',
                  border: '2px solid var(--color-border)',
                  borderRadius: '6px',
                  flexShrink: 0
                }}
              />
              <div className="list-item-content">
                <div className="list-item-title">{item.custom_name || item.product_name}</div>
                {item.quantity > 1 && (
                  <div className="list-item-subtitle">{item.quantity} {item.unit}</div>
                )}
              </div>
              <button className="btn-icon" onClick={() => handleRemove(item)}>
                <Trash2 size={16} color="var(--color-text-light)" />
              </button>
            </div>
          ))}

          {/* Checked items */}
          {checkedItems.length > 0 && (
            <>
              <div className="divider" />
              {checkedItems.map(item => (
                <div key={item.id} className="list-item" style={{ opacity: 0.6 }}>
                  <button
                    className="btn-icon"
                    onClick={() => handleCheck(item)}
                    style={{
                      width: '28px', height: '28px',
                      border: '2px solid var(--color-primary)',
                      borderRadius: '6px',
                      background: 'var(--color-primary)',
                      flexShrink: 0
                    }}
                  >
                    <Check size={16} color="white" />
                  </button>
                  <div className="list-item-content">
                    <div className="list-item-title" style={{ textDecoration: 'line-through' }}>
                      {item.custom_name || item.product_name}
                    </div>
                  </div>
                  <button className="btn-icon" onClick={() => handleRemove(item)}>
                    <Trash2 size={16} color="var(--color-text-light)" />
                  </button>
                </div>
              ))}
            </>
          )}

          {/* Checkout button */}
          {checkedItems.length > 0 && (
            <div style={{ marginTop: 'var(--space-lg)' }}>
              <button className="btn btn-accent btn-full btn-lg" onClick={handleCheckout}>
                <ShoppingBag size={20} />
                {t('shopping.checkout')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ShoppingPage;
